import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useCart } from "../context/CartContext";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ShoppingBag, Ticket, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Pass-Through Checkout landing page (iteration 75, cart-aware iteration 78).
 *
 * Flow the buyer hits after paying Stripe for the exact "delta" needed:
 *   1. Poll GET /api/wallet/pass-through-checkout/{session_id}/status
 *      until `credited=true` (webhook fired token top-up).
 *   2. Once credited, POST /api/wallet/pass-through-checkout/{session_id}/consume
 *      — server-side idempotent, buys the target item OR the whole cart
 *      atomically (all-or-nothing) with the freshly-credited tokens.
 *   3. On success, clear the local cart (cart flow) and redirect to My-*.
 *
 * Errors we handle gracefully:
 *   - session_id missing → not our page, show generic success card
 *   - status endpoint 404 → invalid session, show error
 *   - poll timeout (~40s) → tell buyer to check their inbox/purchases
 *   - consume fails after credit → keep them on the page with retry
 */
const CheckoutComplete = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const { clearCart } = useCart();

  const sessionId = searchParams.get("session_id");
  const itemType = searchParams.get("item_type") || "product";
  const itemId = searchParams.get("item_id");
  const quantity = parseInt(searchParams.get("quantity") || "1", 10);
  const isCart = itemType === "cart";

  // phases: 'waiting' → 'credited' → 'consuming' → 'success' | 'error' | 'timeout'
  const [phase, setPhase] = useState("waiting");
  const [errorMsg, setErrorMsg] = useState("");
  const [orderInfo, setOrderInfo] = useState(null);
  const consumedRef = useRef(false); // guard: only fire consume once per mount
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId || !token) return;
    let cancelled = false;
    const MAX_ATTEMPTS = 20; // ~40s at 2s interval
    const POLL_MS = 2000;

    const poll = async (attempt = 0) => {
      if (cancelled) return;
      setAttempts(attempt + 1);
      try {
        const res = await axios.get(
          `${API}/wallet/pass-through-checkout/${sessionId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (res.data?.credited) {
          setPhase("credited");
          consumeTokens();
          return;
        }
        if (attempt >= MAX_ATTEMPTS - 1) {
          setPhase("timeout");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_MS);
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 404) {
          setErrorMsg("Checkout session not found. If you were charged, your tokens are safe — check your wallet.");
          setPhase("error");
          return;
        }
        // Retry on transient errors up to the cap.
        if (attempt >= MAX_ATTEMPTS - 1) {
          setPhase("timeout");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_MS);
      }
    };

    const consumeTokens = async () => {
      if (consumedRef.current) return;
      consumedRef.current = true;
      setPhase("consuming");
      try {
        // Idempotent server-side: a reload or double-click can never buy twice.
        const res = await axios.post(
          `${API}/wallet/pass-through-checkout/${sessionId}/consume`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        setOrderInfo(res.data || {});
        if (isCart) clearCart?.();
        setPhase("success");
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        if (status === 409 && detail?.code === "consume_in_progress") {
          // Another tab is finalising — re-poll shortly; server will report consumed.
          consumedRef.current = false;
          setTimeout(() => poll(0), POLL_MS);
          return;
        }
        setErrorMsg(
          typeof detail === "string"
            ? detail
            : (detail?.message || "We credited your tokens but the purchase couldn't be finalised automatically. Your Tokens are safe — try again.")
        );
        setPhase("error");
      }
    };

    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, token, isCart, clearCart]);

  // If we landed here without a session_id, something's off — send them home.
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center p-6" data-testid="checkout-complete-no-session">
        <div className="card p-10 max-w-lg text-center">
          <XCircle className="w-12 h-12 text-[#F87171] mx-auto mb-4" />
          <h1 className="font-['Unbounded'] text-2xl text-white mb-2">Checkout Session Missing</h1>
          <p className="text-[#8A92A0] mb-6">We couldn't find your checkout session. If you were charged, your tokens are safe in your wallet.</p>
          <Button onClick={() => navigate("/wallet")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="go-to-wallet-btn">
            Go to My Wallet
          </Button>
        </div>
      </div>
    );
  }

  // Not authenticated — send to sign in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center p-6" data-testid="checkout-complete-unauth">
        <div className="card p-10 max-w-lg text-center">
          <p className="text-white mb-6">Please sign in to complete your purchase.</p>
          <Button onClick={() => navigate("/auth")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const itemLabel = isCart ? "cart" : itemType === "ticket" ? "ticket" : itemType === "experience" ? "experience" : itemType === "collectible" ? "collectible" : "purchase";
  const goToItemHome = () => {
    if (itemType === "ticket") navigate("/my-tickets");
    else if (itemType === "experience") navigate("/my-experiences");
    else if (itemType === "collectible") navigate("/my-collectibles");
    else navigate("/my-purchases");
  };
  const cartOrders = Array.isArray(orderInfo?.orders) ? orderInfo.orders : [];
  const displayTotalCents = orderInfo?.total_paid_cents ?? orderInfo?.amount_cents ?? null;

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="checkout-complete-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
        </div>
      </header>

      <main className="pt-32 pb-16 px-6">
        <div className="max-w-lg mx-auto text-center">
          {(phase === "waiting" || phase === "credited" || phase === "consuming") && (
            <div className="card p-12" data-testid="checkout-complete-waiting">
              <Loader2 className="w-16 h-16 text-[#EC008C] mx-auto mb-6 animate-spin" />
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                {phase === "consuming" ? "Unlocking your " + itemLabel : "Finalising your purchase"}
              </h1>
              <p className="text-[#8A92A0]">
                {phase === "waiting" && `We're confirming your payment (${attempts}/20)…`}
                {phase === "credited" && "Tokens credited — unlocking your item now…"}
                {phase === "consuming" && `Applying tokens to your ${itemLabel}…`}
              </p>
            </div>
          )}

          {phase === "success" && (
            <div className="card p-12" data-testid="checkout-complete-success">
              <div className="w-20 h-20 rounded-full bg-[#8DB87A]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#8DB87A]" />
              </div>
              <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-[#8A92A0] mb-8">
                {isCart
                  ? `Thanks for your purchase — all ${orderInfo?.item_count || cartOrders.length || ""} items in your cart are unlocked.`
                  : `Thanks for your purchase — your ${itemLabel} is unlocked.`}
              </p>
              {displayTotalCents != null && (
                <div className="bg-[#0A0E18] rounded-lg p-6 mb-8 text-left" data-testid="checkout-complete-order-details">
                  <h3 className="font-semibold text-white mb-4">Order Details</h3>
                  <div className="space-y-3 text-sm">
                    {isCart ? (
                      <>
                        {cartOrders.map((o) => (
                          <div key={o.order_id} className="flex justify-between gap-4" data-testid={`checkout-complete-cart-line-${o.item_id}`}>
                            <span className="text-white truncate">{o.name} × {o.quantity}</span>
                            <span className="text-[#8A92A0] whitespace-nowrap">${((o.total_paid_cents || 0) / 100).toFixed(2)}</span>
                          </div>
                        ))}
                        {orderInfo?.tax_cents > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#8A92A0]">Sales tax</span>
                            <span className="text-white">${((orderInfo.tax_cents || 0) / 100).toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-[#8A92A0]">Item type</span>
                          <span className="text-white capitalize">{itemType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#8A92A0]">Quantity</span>
                          <span className="text-white">{orderInfo?.quantity ?? quantity}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between border-t border-[#1A2030] pt-3">
                      <span className="text-[#8A92A0]">Total</span>
                      <span className="text-[#EC008C] font-semibold" data-testid="checkout-complete-total">${(displayTotalCents / 100).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={goToItemHome} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="view-item-btn">
                  {itemType === "ticket" ? <Ticket className="w-4 h-4 mr-2" /> :
                   itemType === "experience" ? <Sparkles className="w-4 h-4 mr-2" /> :
                   <ShoppingBag className="w-4 h-4 mr-2" />}
                  {itemType === "ticket" ? "View My Tickets" : itemType === "experience" ? "View My Booking" : "View My Purchases"}
                </Button>
                <Button onClick={() => navigate("/discover")} variant="outline" className="border-[#1A2030] text-white hover:bg-[#0A0E18]" data-testid="keep-browsing-btn">
                  Continue Browsing
                </Button>
              </div>
            </div>
          )}

          {phase === "timeout" && (
            <div className="card p-12" data-testid="checkout-complete-timeout">
              <div className="w-20 h-20 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#EC008C] animate-spin" />
              </div>
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">Payment is Processing</h1>
              <p className="text-[#8A92A0] mb-2">Your bank is taking a moment to settle the charge.</p>
              <p className="text-[#8A92A0] mb-8">
                Your tokens will land in your wallet automatically as soon as it clears. You can safely leave this page.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/wallet")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="timeout-wallet-btn">
                  Check My Wallet
                </Button>
                <Button onClick={() => window.location.reload()} variant="outline" className="border-[#1A2030] text-white hover:bg-[#0A0E18]" data-testid="timeout-retry-btn">
                  Retry
                </Button>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="card p-12" data-testid="checkout-complete-error">
              <div className="w-20 h-20 rounded-full bg-[#F87171]/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-[#F87171]" />
              </div>
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">Something's Off</h1>
              <p className="text-[#8A92A0] mb-8">{errorMsg || "We couldn't finish your purchase. Please try again."}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate("/wallet")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="error-wallet-btn">
                  Check My Wallet
                </Button>
                <Button
                  onClick={() => (isCart || !itemId) ? navigate("/discover") : navigate(`/${itemType === "ticket" ? "events" : "products"}/${itemId}`)}
                  variant="outline"
                  className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                  data-testid="error-retry-btn"
                >
                  {isCart || !itemId ? "Back to Store" : "Back to Item"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default CheckoutComplete;
